import Logo from "@/components/Header/Logo";
import "./Privacy.css";

// --------------------------------------------------------------------------------------

export default function Privacy() {
	// The date should be updated manually when the policy changes
	const lastUpdatedDate = "June 19, 2025";

	// --------------------------------------------------------------------------------------

	return (
		<>
			{/* Page head */}
			<title>BrainEm ￭ Privacy Policy</title>
			<meta
				name="description"
				content="This page details the privacy policy for the brainem.co website, including information on data collection, cookies, and third-party services."
			/>

			{/* ------------------------------------------------------------------------------ */}

			{/* Page content */}
			<div className="privacy-wrapper">
				<header>
					<Logo />
				</header>

				{/* Hero heading */}
				<div className="privacy-heading">
					<h1>Privacy Policy</h1>
					<p>Last Updated: {lastUpdatedDate}</p>
				</div>

				{/* ------------------------------------------------------------------------------ */}

				{/* Sections */}
				<main className="privacy-main">
					<section>
						<h2 className="accent-blue">Summary (TL;DR)</h2>
						<p>
							I take your privacy seriously. Here's a summary of my practices:
						</p>

						<ul>
							<li>
								This site does not ask you for personal data. To protect the
								site from threats, Cloudflare's security features may process
								traffic data (like your IP address) and set essential security
								cookies.
							</li>
							<li>I do not use cookies for tracking or advertising.</li>
							<li>
								I use privacy-focused analytics from Cloudflare that are
								anonymous and aggregated.
							</li>
							<li>
								When an error occurs (and only for visitors outside the
								EEA/EFTA), an anonymised session replay is captured via Sentry;
								it uses session storage, not cookies.
							</li>
							<li>
								A service worker caches static assets and API responses so the
								site works offline. No personal data is cached.
							</li>
							<li>This site does not contain any ads.</li>
							<li>
								If you create a shareable scenario, the app stores your
								calculator selections in a Cloudflare KV store. The entry
								remains for ten years and contains only those selections – no
								personal data is saved.
							</li>
						</ul>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Introduction</h2>
						<p>
							This is the privacy policy for brainem.co. I am committed to
							protecting your privacy. This document outlines how I handle
							information on this website.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Data We Process</h2>
						<p>
							This website is an interactive calculator for exploring brain
							emulation costs. It does not request personal details, and no
							personal data is stored on the site. Cloudflare, which hosts and
							secures the site, may temporarily log your IP address and related
							technical data for security purposes.
						</p>

						<ul>
							<li>
								<strong>No User Accounts:</strong> You do not need to sign up or
								log in to use this site.
							</li>
							<li>
								<strong>No Comments or Forms:</strong> There are no comment
								sections or direct contact forms that would collect personal
								data. The feedback link directs to an external Google Form,
								which has its own privacy policy.
							</li>
							<li>
								<strong>No Cookies for Tracking:</strong> This website does not
								use cookies for advertising or tracking purposes.
							</li>
							<li>
								<strong>Local Storage for Calculator:</strong> Your selections
								in the calculator are saved in <code>localStorage</code> and{" "}
								<code>sessionStorage</code>. This data remains on your device,
								contains no personal identifiers, and is not used for tracking.
							</li>
							<li>
								<strong>Error Session Replay:</strong> For visitors outside the
								EEA/EFTA, Sentry records an anonymised replay of the page when
								an error happens. All on-screen text is masked, media elements
								are blocked, no cookies are set, and IP addresses are removed
								before storage.
							</li>
							<li>
								Cloudflare may retain security logs, including your IP address,
								for a limited period{" "}
								<a
									href="https://www.cloudflare.com/privacypolicy/#11-data-retention"
									target="_blank"
									rel="noopener noreferrer"
								>
									(see their policy for details)
								</a>{" "}
								to help detect and mitigate abuse.
							</li>
						</ul>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>How and Why We Use Third-Party Services</h2>
						<p>
							This website uses services provided by Cloudflare for web hosting,
							security, and analytics. Fonts are self-hosted, so no external
							font providers are contacted. The use of these services is
							essential for the secure, reliable, and performant operation of
							the site.
						</p>

						<div className="privacy-subsection">
							<h3>Website Analytics</h3>
							<p>
								To understand how the website is used and to improve it, I use
								Cloudflare Web Analytics. This is a privacy-first analytics
								service.
							</p>
							<ul>
								<li>It does not use cookies.</li>
								<li>
									It does not track individual users or "fingerprint" them by IP
									address, User Agent, or other data.
								</li>
								<li>All metrics are aggregated and anonymous.</li>
							</ul>
						</div>

						<div className="privacy-subsection">
							<h3>Fonts</h3>
							<p>
								This site uses the Funnel Sans and Funnel Display font families,
								which are self-hosted. No external font providers are involved,
								so your browser does not contact third-party font services when
								loading the site.
							</p>
						</div>

						<div className="privacy-subsection">
							<h3>Voluntary Contributions</h3>
							<p>
								This site accepts voluntary contributions via Stripe. When you
								choose to donate, your payment details are handled directly by
								Stripe – not by this website. Stripe processes your payment data
								in accordance with its own{" "}
								<a
									href="https://stripe.com/privacy"
									target="_blank"
									rel="noopener noreferrer"
								>
									privacy policy
								</a>
								. The site itself never sees or stores your full card number or
								other sensitive payment information.
							</p>
						</div>

						<div className="privacy-subsection">
							<h3>Security</h3>
							<p>
								To protect this website from malicious activity, I use several
								of Cloudflare's security features, including their Web
								Application Firewall (WAF), Bot Fight Mode, and DDoS mitigation.
								These services work by processing traffic data, including
								visitors' IP addresses, to identify and block threats. Under the
								General Data Protection Regulation (GDPR), the legal basis for
								this processing is our legitimate interest. This means that
								processing this data is necessary to ensure the security,
								integrity, and availability of this website and its services,
								and this interest is not overridden by your data protection
								interests or fundamental rights and freedoms.
							</p>
							<p>
								For more detailed information on Cloudflare's data handling and
								compliance, you can review their{" "}
								<a
									href="https://www.cloudflare.com/trust-hub/gdpr/"
									target="_blank"
									rel="noopener noreferrer"
								>
									official GDPR policy
								</a>
								.
							</p>
						</div>

						<div className="privacy-subsection">
							<h3>Error Monitoring</h3>
							<p>
								To help me identify and fix problems on the site, I use{" "}
								<a
									href="https://sentry.io"
									target="_blank"
									rel="noopener noreferrer"
								>
									Sentry
								</a>{" "}
								to log runtime errors. This monitoring runs only when you are
								outside the European Economic Area (EEA) and the European Free
								Trade Association (EFTA). I determine this using the country
								code header added by Cloudflare.
							</p>

							<p>
								Within Sentry, the "Data Scrubber", "Prevent Storing of IP
								Addresses" and "Enhanced Privacy" options are enabled for all
								users of the site to minimize any personal data processed.
							</p>
							<p>
								Sentry's <em>Session Replay</em> feature is configured with{" "}
								<code>replaysSessionSampleRate&nbsp;=&nbsp;0.0</code> and{" "}
								<code>replaysOnErrorSampleRate&nbsp;=&nbsp;1.0</code>. This
								means a replay is recorded only when an error is captured. The
								SDK keeps all default privacy safeguards: it masks every text
								node with "*" and blocks images and other media. These replays
								help reproduce issues while minimizing personal data.
							</p>
						</div>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Cookies and Similar Technologies</h2>
						<p>
							This website does not use cookies for tracking or advertising
							purposes.
						</p>
						<p>
							However, Cloudflare, our security provider, uses cookies to
							protect the site from malicious bots and ensure its reliable
							operation. Specifically, cookies such as <code>__cf_bm</code> (Bot
							Management cookie) and <code>cf_clearance</code> (Challenge
							Clearance cookie) may be placed on your device. These cookies are
							essential for distinguishing between human and automated traffic,
							and for successfully passing security challenges. Cloudflare
							states these cookies are "strictly necessary" for their security
							services to function and do not track users across different
							websites or for advertising. You can find more general information
							about Cloudflare's cookies in their{" "}
							<a
								href="https://developers.cloudflare.com/fundamentals/reference/policies-compliances/cloudflare-cookies/"
								target="_blank"
								rel="noopener noreferrer"
							>
								documentation
							</a>
							.
						</p>
						<p>
							Under GDPR and the ePrivacy Directive, such strictly necessary
							cookies do not require user consent. These cookies do not store
							personal data that directly identifies you beyond what is needed
							for their security function and are not used to track your
							activity outside of this site.
						</p>
						<p>
							Sentry&nbsp;Session&nbsp;Replay does not use cookies; it stores
							temporary data in your browser's <code>sessionStorage</code>,
							which is cleared when the tab or window closes.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>International Data Transfers</h2>
						<p>
							When you use this website, brainem.co is the Data Controller for
							any personal data processed. For the essential services of web
							hosting, security, and analytics, we use Cloudflare, Inc.
							Cloudflare acts as a Data Processor on our behalf.
						</p>
						<p>
							Cloudflare is a global company headquartered in the United States.
							As such, when you access this site, technical data such as your IP
							address (processed for security and operational purposes) may be
							transferred to and processed by Cloudflare's servers located in
							various countries, including the United States.
						</p>
						<p>
							For visitors from the European Economic Area (EEA), this
							constitutes an international data transfer. We rely on
							Cloudflare's commitments to ensure lawful data transfers.
							Cloudflare implements safeguards for such transfers, primarily
							through Standard Contractual Clauses (SCCs), which are contractual
							commitments approved by the European Commission to ensure adequate
							data protection when data is transferred outside the EEA. These
							SCCs form part of our data processing agreement with Cloudflare.
							You can find more information on Cloudflare's data handling
							practices, their commitment to GDPR, and their data transfer
							safeguards in their{" "}
							<a
								href="https://www.cloudflare.com/trust-hub/gdpr/"
								target="_blank"
								rel="noopener noreferrer"
							>
								GDPR policy
							</a>{" "}
							and related trust documentation.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>GDPR Compliance</h2>
						<p>
							The General Data Protection Regulation (GDPR) is a regulation in
							EU law on data protection and privacy. As this site is designed to
							minimize direct collection and storage of personal data, my
							practices are aligned with the principles of the GDPR. The primary
							personal data processed is your IP address by our security
							provider Cloudflare, for which the handling is detailed in this
							policy and based on legitimate interest, as permitted under GDPR.
						</p>

						<div className="privacy-subsection">
							<h3>Data Subject Rights under GDPR</h3>
							<p>
								If you are a visitor from the European Economic Area (EEA), the
								General Data Protection Regulation (GDPR) grants you specific
								rights regarding your personal data. These include the right to
								access, rectify, erase, restrict processing of, and object to
								processing of your personal data, as well as the right to data
								portability.
							</p>
							<p>
								As this website does not ask for, collect, or store your
								personal data in a way that allows for individual identification
								(e.g., no user accounts, no direct submission forms leading to
								databases I control), the direct applicability and exercise of
								these rights are limited in this context. For instance, there is
								no personal data stored that you could directly access or
								request to be erased from my systems.
							</p>
							<p>
								However, if you have any inquiries or concerns about how your
								data might be handled, particularly regarding the temporary
								processing of IP addresses by essential third-party services
								like Cloudflare for security, you are welcome to contact me. You
								also have the right to lodge a complaint with a supervisory
								authority.
							</p>
						</div>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>US Privacy Laws</h2>
						<p>
							This website is visited by users from various regions, including
							the United States. Numerous US states have enacted comprehensive
							privacy laws, including states like California (CCPA/CPRA),
							Virginia (VCDPA), Colorado (CPA), and others. These laws grant
							consumers various rights regarding their personal data.
						</p>
						<p>
							I aim to respect the privacy of all visitors. However, as this
							website does not collect or store personal data in a way that
							would allow for individual identification or enable specific
							consumer rights requests (like access, deletion, or opt-out of
							sale, as defined under these various state laws), specific
							mechanisms for these requests are not implemented. This site does
							not "sell" data as commonly defined by these laws.
						</p>
						<p>
							This site does not respond to "Do Not Track" (DNT) signals from
							browsers, as there is no tracking of personal data to begin with.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Children's Privacy</h2>
						<p>
							This website is not intended for or directed at children under the
							age of 16. I do not knowingly collect any personal data from
							children under 16.
						</p>
						<p>
							If you are a parent or guardian and you believe that your child
							under 16 has provided personal data to this website, please
							contact me using the contact information below so that I can take
							appropriate action.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Technical Security Measures</h2>
						<p>
							Beyond using Cloudflare's services, this site implements a strong
							Content Security Policy (CSP) and Permissions Policy. These are
							technical headers sent with the website that instruct your browser
							to block certain types of security threats and disable browser
							features that are not needed (like access to your camera or
							microphone). This is a proactive measure to protect both you and
							the website.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>External Links</h2>
						<p>
							This website contains links to other websites for providing
							references, giving credit, collecting feedback, and more. These
							links may point to services like Google&nbsp;Sheets,
							Google&nbsp;Forms, dnarna.co, Asimov&nbsp;Press, Reddit,
							Harvard&nbsp;Brain&nbsp;Science, BioRender, and others.
						</p>
						<p>
							Please be aware that I am not responsible for the privacy
							practices of these other sites. When you leave this website, I
							encourage you to read the privacy policy of each website you
							visit. This privacy policy applies solely to information handled
							by brainem.co.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Disclaimer</h2>
						<p>
							Please note that I am a software developer, not a legal
							professional. This privacy policy is provided for informational
							purposes only and represents a good-faith effort to be transparent
							about my data handling practices. It does not constitute legal
							advice.
						</p>
						<p>
							For specific legal advice regarding your rights or compliance
							obligations, please consult with a qualified legal professional.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Changes to This Policy</h2>
						<p>
							I may update this privacy policy from time to time. The "Last
							Updated" date at the top of this page will always be current.
						</p>
					</section>

					{/* ---------------------------------------------------------------------- */}

					<section>
						<h2>Contact</h2>
						<p>
							If you have any questions about this privacy policy, you can
							contact me at{" "}
							<a href="mailto:dave@dnarna.co" className="email-link">
								dave@dnarna.co
							</a>
							.
						</p>
					</section>
				</main>
			</div>
		</>
	);
}
